import React from "react";
import { Alert, Badge, Button, Card, Modal, Spinner, Table } from "react-bootstrap";
import { notifySuccess } from "../../../components/AppToast";
import { useActivateCompanyUser, useCompanyUsers, useStaffPermissions } from "../hooks";

const HEADING_ID = "company-users-modal-title";

const ROLE_LABELS = {
  OWNER: "Propietario",
  TEACHER: "Docente",
  STUDENT: "Estudiante",
};

function rolesLabel(roles) {
  return (roles ?? []).map((r) => ROLE_LABELS[r] ?? r).join(", ");
}

// Extraído porque se renderiza dos veces (fila de escritorio + tarjeta móvil),
// y solo debe aparecer cuando hay algo que activar (DoD: acción visible
// únicamente cuando corresponda).
function ActivateUserButton({ user, onActivate, disabled, className }) {
  return (
    <Button
      size="sm"
      className={className}
      style={{ minHeight: 44 }}
      variant="outline-success"
      onClick={() => onActivate(user)}
      disabled={disabled}
      aria-label={`Activar cuenta de ${user.email}`}
    >
      Activar
    </Button>
  );
}

/**
 * Usuarios de una academia + activación manual de cuentas atascadas.
 *
 * Parche operativo mientras el proveedor de correo transaccional esté caído:
 * un owner/estudiante cuyo correo de activación nunca llegó queda con
 * `User.is_active=False` para siempre (no puede loguear ni reenviárselo). Este
 * modal le da a soporte/administración una salida manual sin tocar la BD a mano.
 *
 * Igual que `ConfirmDeactivateModal`: siempre montado, controlado por
 * `company` (null = oculto) en vez de un booleano `show` separado.
 */
function CompanyUsersModal({ company, onHide }) {
  const companyId = company?.id;
  const { data: users, isLoading, isError } = useCompanyUsers(companyId);
  const activateUser = useActivateCompanyUser(companyId);
  const { can } = useStaffPermissions();
  const canActivate = can("companies", "suspend");

  function handleActivate(user) {
    activateUser.mutate(user.id, {
      onSuccess: () => notifySuccess(`Cuenta de ${user.email} activada.`),
    });
  }

  return (
    <Modal
      show={Boolean(company)}
      onHide={onHide}
      aria-labelledby={HEADING_ID}
      aria-modal="true"
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title id={HEADING_ID}>Usuarios de {company?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && (
          <div className="text-center py-4">
            <Spinner aria-label="Cargando usuarios" />
          </div>
        )}
        {isError && (
          <Alert variant="danger">No se pudo cargar la lista de usuarios.</Alert>
        )}
        {activateUser.isError && (
          <Alert variant="danger" className="small">
            {activateUser.error?.response?.data?.message ??
              "No se pudo activar la cuenta."}
          </Alert>
        )}
        {!isLoading && !isError && (users?.length ?? 0) === 0 && (
          <p className="text-muted mb-0">Esta academia no tiene usuarios registrados.</p>
        )}
        {!isLoading && !isError && (users?.length ?? 0) > 0 && (
          <>
            {/* ── Desktop: tabla (d-none d-sm-block) ── */}
            <div className="d-none d-sm-block table-responsive">
              <Table hover bordered size="sm" className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Usuario</th>
                    <th>Roles</th>
                    <th>Estado</th>
                    <th>Alta</th>
                    {canActivate && <th>Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <div>{u.name}</div>
                        <div className="text-muted small">{u.email}</div>
                      </td>
                      <td>{rolesLabel(u.roles)}</td>
                      <td>
                        <Badge bg={u.is_active ? "success" : "warning"}>
                          {u.is_active ? "Activo" : "Pendiente"}
                        </Badge>
                      </td>
                      <td>
                        {u.date_joined
                          ? new Date(u.date_joined).toLocaleDateString("es-CO")
                          : "—"}
                      </td>
                      {canActivate && (
                        <td>
                          {!u.is_active && (
                            <ActivateUserButton
                              user={u}
                              onActivate={handleActivate}
                              disabled={activateUser.isPending}
                            />
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* ── Mobile: tarjetas (d-sm-none) ── */}
            <div className="d-sm-none">
              {users.map((u) => (
                <Card key={u.id} className="mb-2">
                  <Card.Body className="py-2 px-3">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <strong>{u.name}</strong>
                        <div className="text-muted small">{u.email}</div>
                      </div>
                      <Badge bg={u.is_active ? "success" : "warning"}>
                        {u.is_active ? "Activo" : "Pendiente"}
                      </Badge>
                    </div>
                    <div className="small text-muted mt-1">{rolesLabel(u.roles)}</div>
                    {canActivate && !u.is_active && (
                      <ActivateUserButton
                        user={u}
                        onActivate={handleActivate}
                        disabled={activateUser.isPending}
                        className="mt-2"
                      />
                    )}
                  </Card.Body>
                </Card>
              ))}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export { CompanyUsersModal };
